import { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  COMPARATIVE_NUMBER_IN_HOURS_10,
  COMPARATIVE_NUMBER_IN_HOURS_12,
  COMPARATIVE_NUMBER_IN_HOURS_24,
  COMPARATIVE_NUMBER_IN_HOURS_59,
  timeoutDuration
} from 'utils/constants/dateInfo/time-numbers';
import { fancyTimeFormat } from 'utils/string-manipulation/format-time-from-second';
import { Divider } from 'newcomponents/UI/Divider';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';
import './index.less';

type TClockProps = {
  configs: {
    twentyFourHours: number;
    loginDuration: boolean;
    specifiedFormats: boolean;
    showDate: boolean;
  };
};

const clockDefaultProps = {
  configs: {
    twentyFourHours: 0,
    loginDuration: true,
    specifiedFormats: false,
    showDate: false
  }
};

export const Clock = (props: TClockProps = clockDefaultProps): ReactElement => {
  const [realTime, setRealTime] = useState('');
  const [dayType, setDayType] = useState('');
  const [loginDurationClock, setLoginDurationClock] = useState(0);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const user = useSelector(getUser);

  const setupClock = (sec: number, min: number, hour: number) => {
    if (sec < COMPARATIVE_NUMBER_IN_HOURS_59) {
      sec++;
    } else {
      sec = 0;

      if (min < COMPARATIVE_NUMBER_IN_HOURS_59) {
        min++;
      } else {
        min = 0;

        if (hour < COMPARATIVE_NUMBER_IN_HOURS_24) {
          hour++;
        } else {
          hour = 0;
        }
      }
    }

    let dayType = '';
    let h: string | number = hour;
    const m = min < COMPARATIVE_NUMBER_IN_HOURS_10 ? `0${min}` : min;
    const s = sec < COMPARATIVE_NUMBER_IN_HOURS_10 ? `0${sec}` : sec;

    if (!props.configs.twentyFourHours) {
      if (hour > COMPARATIVE_NUMBER_IN_HOURS_12) {
        h = hour - COMPARATIVE_NUMBER_IN_HOURS_12;
        dayType = 'PM';
      } else {
        dayType = 'AM';
      }
    }

    if (h < COMPARATIVE_NUMBER_IN_HOURS_10) {
      h = `0${h}`;
    }

    setRealTime(`${h}:${m}:${s}`);
    setDayType(`${dayType}`);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const newDate = new Date();
      setupClock(
        newDate.getSeconds(),
        newDate.getMinutes(),
        newDate.getHours()
      );
      setLoginDurationClock(prev => prev + 1);
    }, timeoutDuration.DURATION_1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (props.configs.loginDuration && isLoggedIn && user.last_login_date) {
      setLoginDurationClock(dayjs().unix() - user.last_login_date);
    }
  }, [isLoggedIn, user]);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (+props.configs.specifiedFormats) {
      return `${year}/${month}/${day}`;
    } else {
      return `${day}/${month}/${year}`;
    }
  };

  const date = new Date();
  const formattedDate = formatDate(date);
  const fancyTime =
    props.configs.loginDuration && isLoggedIn ? (
      <span>
        (
        <span className="loginDurationClock">
          {fancyTimeFormat(loginDurationClock)}
        </span>
        )
      </span>
    ) : null;

  return (
    <div className="clock__element">
      <span>
        <span style={{ minWidth: '64px', display: 'inline-flex' }}>
          {realTime}
        </span>
        <span style={{ paddingRight: '2px' }}>{dayType}</span>
        {fancyTime}
        {+props.configs.showDate && realTime ? (
          <>
            <Divider
              type="vertical"
              distance={8}
              className="clock__element--divider"
            />
            <span
              style={{ width: '80px', display: 'inline-flex' }}
              data-testid="clock-widget-date"
            >
              {formattedDate}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
};
